<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
import { computed, onMounted, reactive, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const DEFAULT_ITEMS = [
  {
    id: 'deposit_not_received',
    title: 'Deposit not credited',
    reply:
      'Dear player, if your deposit has not been credited yet, please provide your payment screenshot or transaction ID. We will check it for you as soon as possible.',
  },
  {
    id: 'withdrawal_issue',
    title: 'Withdrawal issue',
    reply:
      'Dear player, please provide your withdrawal order details or a screenshot. We will verify the withdrawal status for you.',
  },
  {
    id: 'account_issue',
    title: 'Account or login issue',
    reply:
      'Dear player, please describe your account or login issue and provide your user ID. We will help you check it.',
  },
  {
    id: 'human_support',
    title: 'Contact human support',
    reply:
      'Dear player, we have received your request. A customer service agent will check your issue and reply as soon as possible.',
  },
];

const DEFAULT_CONFIG = {
  enabled: true,
  title: 'Hot questions',
  inbox_ids: [],
  items: DEFAULT_ITEMS,
};

const store = useStore();
const { currentAccount } = useAccount();

const form = reactive({
  enabled: DEFAULT_CONFIG.enabled,
  title: DEFAULT_CONFIG.title,
  inbox_ids: [],
  items: DEFAULT_ITEMS.map(item => ({ ...item })),
});

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const isFetchingInboxes = computed(
  () => store.getters['inboxes/getUIFlags']?.isFetching
);
const isUpdating = computed(
  () => store.getters['accounts/getUIFlags']?.isUpdating
);

const allInboxIds = computed(() =>
  inboxes.value.map(inbox => Number(inbox.id)).filter(Boolean)
);

const areAllInboxesSelected = computed(() => {
  if (!allInboxIds.value.length) return false;
  return allInboxIds.value.every(id => form.inbox_ids.includes(id));
});

const isAllInboxSelection = computed(
  () => !form.inbox_ids.length || areAllInboxesSelected.value
);

const selectedInboxNames = computed(() => {
  const ids = form.inbox_ids.map(Number);
  if (isAllInboxSelection.value) return '全部收件箱';
  const names = inboxes.value
    .filter(inbox => ids.includes(Number(inbox.id)))
    .map(inbox => inbox.name);
  return names.length ? names.join('、') : '未选择收件箱';
});

const validItems = computed(() =>
  form.items.filter(item => item.title.trim() && item.reply.trim())
);

const previewText = computed(() => {
  if (!form.enabled) return '常见问题列表未启用，玩家进线后不会自动展示 FAQ。';
  return `启用后，玩家第一条消息进入 ${selectedInboxNames.value} 时，会自动收到“${form.title || DEFAULT_CONFIG.title}”列表，共 ${validItems.value.length} 个可点击问题。`;
});

const normalizeItems = items => {
  const source = Array.isArray(items) && items.length ? items : DEFAULT_ITEMS;
  return source.map((item, index) => ({
    id: String(item?.id || `faq_${Date.now()}_${index}`),
    title: String(item?.title || ''),
    reply: String(item?.reply || ''),
  }));
};

const normalizeConfig = config => ({
  enabled: config?.enabled ?? DEFAULT_CONFIG.enabled,
  title: String(config?.title || DEFAULT_CONFIG.title),
  inbox_ids: Array(config?.inbox_ids || [])
    .map(Number)
    .filter(Boolean),
  items: normalizeItems(config?.items),
});

const applyAccountConfig = () => {
  const nextConfig = normalizeConfig(
    currentAccount.value?.settings?.kefubao_faq_menu
  );
  form.enabled = Boolean(nextConfig.enabled);
  form.title = nextConfig.title;
  form.inbox_ids = [...nextConfig.inbox_ids];
  form.items = nextConfig.items.map(item => ({ ...item }));
};

const toggleInbox = inboxId => {
  const id = Number(inboxId);
  if (!form.inbox_ids.length) form.inbox_ids = [...allInboxIds.value];
  const index = form.inbox_ids.indexOf(id);
  if (index >= 0) form.inbox_ids.splice(index, 1);
  else form.inbox_ids.push(id);
};

const selectAllInboxes = () => {
  form.inbox_ids = [];
};

const addItem = () => {
  form.items.push({
    id: `faq_${Date.now()}`,
    title: '',
    reply: '',
  });
};

const removeItem = index => {
  form.items.splice(index, 1);
};

const resetDefaultItems = () => {
  form.items = DEFAULT_ITEMS.map(item => ({ ...item }));
};

const saveConfig = async () => {
  const items = form.items
    .map((item, index) => ({
      id: String(item.id || `faq_${Date.now()}_${index}`),
      title: item.title.trim(),
      reply: item.reply.trim(),
    }))
    .filter(item => item.title && item.reply);

  if (!items.length) {
    useAlert('请至少保留一条完整的常见问题');
    return;
  }

  try {
    await store.dispatch('accounts/update', {
      kefubao_faq_menu: {
        enabled: form.enabled,
        title: form.title.trim() || DEFAULT_CONFIG.title,
        inbox_ids: form.inbox_ids.map(Number).filter(Boolean),
        items,
      },
    });
    form.title = form.title.trim() || DEFAULT_CONFIG.title;
    form.items = items.map(item => ({ ...item }));
    useAlert('常见问题配置已保存');
  } catch (error) {
    useAlert('保存失败，请稍后重试');
  }
};

watch(
  () => currentAccount.value?.settings?.kefubao_faq_menu,
  applyAccountConfig,
  { immediate: true }
);

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <SettingsLayout :is-loading="isFetchingInboxes" :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        title="常见问题"
        description="配置玩家进线后自动展示的问题列表。玩家点击问题后，系统会发送对应的默认回复。"
      />
    </template>

    <template #body>
      <div class="max-w-6xl">
        <div
          class="mb-5 flex items-center justify-between rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 shadow-sm"
        >
          <div class="flex min-w-0 items-center gap-3">
            <span
              class="inline-flex size-10 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <Icon icon="i-lucide-list-checks" class="size-5" />
            </span>
            <div>
              <h2 class="m-0 text-base font-semibold text-n-slate-12">
                前台常见问题列表
              </h2>
              <p class="m-0 mt-0.5 text-sm text-n-slate-11">
                玩家首次进线后自动展示，点击后按配置发送回复。
              </p>
            </div>
          </div>
          <label
            class="relative inline-flex cursor-pointer items-center gap-3 rounded-full border border-n-weak bg-n-background py-2 pl-4 pr-2 text-sm font-medium text-n-slate-12"
          >
            <span>{{ form.enabled ? '已启用' : '未启用' }}</span>
            <input
              v-model="form.enabled"
              class="peer sr-only"
              type="checkbox"
            />
            <span
              class="h-6 w-11 rounded-full bg-n-slate-6 transition peer-checked:bg-n-brand"
            />
            <span
              class="absolute right-7 size-5 rounded-full bg-white shadow transition peer-checked:translate-x-5"
            />
          </label>
        </div>

        <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <section
            class="overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-sm"
          >
            <div class="border-b border-n-weak bg-n-solid-1 px-5 py-4">
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                列表配置
              </h3>
              <p class="m-0 mt-1 text-sm leading-6 text-n-slate-11">
                建议问题标题保持简短，回复内容写成玩家可直接理解的完整话术。
              </p>
            </div>

            <div class="space-y-5 p-5">
              <div class="grid gap-4 md:grid-cols-2">
                <label class="grid gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    列表标题
                  </span>
                  <input
                    v-model="form.title"
                    class="h-11 rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                    placeholder="例如：Hot questions"
                    type="text"
                  />
                </label>
              </div>

              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                      作用收件箱
                    </h3>
                    <p class="m-0 mt-1 text-sm text-n-slate-11">
                      当前：{{ selectedInboxNames }}
                    </p>
                  </div>
                  <button
                    class="h-8 rounded-lg border border-n-weak px-3 text-sm text-n-slate-12 hover:bg-n-alpha-black2"
                    type="button"
                    @click="selectAllInboxes"
                  >
                    全部收件箱
                  </button>
                </div>

                <div class="grid gap-2 md:grid-cols-2">
                  <label
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    class="flex cursor-pointer items-center gap-2 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 hover:border-n-brand"
                  >
                    <input
                      :checked="
                        isAllInboxSelection ||
                        form.inbox_ids.includes(Number(inbox.id))
                      "
                      type="checkbox"
                      @change="toggleInbox(inbox.id)"
                    />
                    <span class="min-w-0 truncate">{{ inbox.name }}</span>
                  </label>
                </div>
              </div>

              <div class="space-y-3">
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                      问题与回复
                    </h3>
                    <p class="m-0 mt-1 text-sm text-n-slate-11">
                      当前有效问题 {{ validItems.length }} 条
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      class="h-8 rounded-lg border border-n-weak px-3 text-sm text-n-slate-12 hover:bg-n-alpha-black2"
                      type="button"
                      @click="resetDefaultItems"
                    >
                      恢复默认
                    </button>
                    <button
                      class="inline-flex h-8 items-center gap-1.5 rounded-lg bg-n-brand px-3 text-sm font-medium text-white"
                      type="button"
                      @click="addItem"
                    >
                      <Icon icon="i-lucide-plus" class="size-4" />
                      新增问题
                    </button>
                  </div>
                </div>

                <div
                  v-for="(item, index) in form.items"
                  :key="item.id"
                  class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
                >
                  <div class="mb-3 flex items-center justify-between gap-3">
                    <p class="m-0 text-sm font-semibold text-n-slate-12">
                      问题 {{ index + 1 }}
                    </p>
                    <button
                      class="inline-flex size-8 items-center justify-center rounded-lg text-n-ruby-9 hover:bg-n-ruby-3"
                      type="button"
                      @click="removeItem(index)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>

                  <div class="grid gap-3">
                    <label class="grid gap-2">
                      <span class="text-sm font-medium text-n-slate-12">
                        问题标题
                      </span>
                      <input
                        v-model="item.title"
                        class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-brand"
                        placeholder="例如：Deposit not credited"
                        type="text"
                      />
                    </label>
                    <label class="grid gap-2">
                      <span class="text-sm font-medium text-n-slate-12">
                        默认回复
                      </span>
                      <textarea
                        v-model="item.reply"
                        class="min-h-24 rounded-lg border border-n-weak bg-n-background p-3 text-sm outline-none focus:border-n-brand"
                        placeholder="玩家点击该问题后自动发送的回复"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <div class="mb-2 flex items-center gap-2">
                  <Icon icon="i-lucide-info" class="size-4 text-n-brand" />
                  <p class="m-0 text-sm font-semibold text-n-slate-12">
                    当前规则预览
                  </p>
                </div>
                <p class="m-0 text-sm leading-6 text-n-slate-11">
                  {{ previewText }}
                </p>
              </div>

              <div
                class="flex items-center justify-end gap-3 border-t border-n-weak pt-4"
              >
                <button
                  class="h-9 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 transition hover:bg-n-alpha-black2"
                  type="button"
                  @click="applyAccountConfig"
                >
                  取消修改
                </button>
                <button
                  class="h-9 rounded-lg bg-n-brand px-4 text-sm font-medium text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="isUpdating"
                  type="button"
                  @click="saveConfig"
                >
                  {{ isUpdating ? '保存中...' : '保存配置' }}
                </button>
              </div>
            </div>
          </section>

          <aside
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
          >
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">使用说明</h3>
            <div class="mt-3 space-y-3 text-sm leading-6 text-n-slate-11">
              <p class="m-0">
                常见问题只会在玩家发送第一条公开消息后展示一次。
              </p>
              <p class="m-0">
                玩家点击某个问题后，系统会立刻用该问题对应的话术回复。
              </p>
              <p class="m-0">
                如果只想让某个收件箱展示 FAQ，请在“作用收件箱”里只勾选
                对应收件箱。
              </p>
              <p class="m-0">
                所有问题标题会直接展示给玩家，建议保持英语或目标前台语言。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
